// AP Statistics Curriculum - Units 1-3
import { Unit } from '../components/GradeLessonPage';

export const unit1: Unit = {
  id: 'unit1',
  title: 'Unit 1: Exploring One-Variable Data',
  description: 'Analyzing data distributions, histograms, box plots, measures of center and spread',
  weight: '15%–23%',
  topics: [
    {
      id: 'categorical-data',
      title: 'Representing Categorical Data',
      description: 'Frequency tables, bar charts, and pie charts',
      skills: [
        {
          id: 'frequency-tables',
          name: 'Constructing Frequency Tables',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `freq-${i + 1}`,
            question: i === 0 ? 'A survey of 50 students asked their favorite subject. Math: 15, Science: 12, English: 10, History: 8, Art: 5. Create a frequency table showing counts and relative frequencies.' :
                     i === 1 ? 'Given the data set of car colors: Red, Blue, Red, White, Black, Red, Blue, White, Red, Black. Create a frequency table.' :
                     i === 2 ? 'A class of 30 students reported their birth months. January: 3, February: 2, March: 4, April: 3, May: 2, June: 3, July: 2, August: 3, September: 2, October: 3, November: 2, December: 1. Calculate the relative frequency for each month.' :
                     `Create a frequency table for the following categorical data: ${['Category A', 'Category B', 'Category C'][i % 3]} appears ${Math.floor(Math.random() * 20) + 5} times.`,
            answer: i === 0 ? 'Frequency Table:\nSubject | Count | Relative Frequency\nMath | 15 | 0.30\nScience | 12 | 0.24\nEnglish | 10 | 0.20\nHistory | 8 | 0.16\nArt | 5 | 0.10\nTotal | 50 | 1.00' :
                   i === 1 ? 'Frequency Table:\nColor | Count | Relative Frequency\nRed | 4 | 0.40\nBlue | 2 | 0.20\nWhite | 2 | 0.20\nBlack | 2 | 0.20\nTotal | 10 | 1.00' :
                   i === 2 ? 'Each month has relative frequency = count/30. For example, January = 3/30 = 0.10, March = 4/30 ≈ 0.133, etc.' :
                   'Solution with frequency table showing counts and relative frequencies.',
            steps: [
              'Count the frequency of each category',
              'Calculate relative frequency by dividing each count by the total',
              'Verify that all relative frequencies sum to 1.00',
              'Present data in a clear table format'
            ]
          }))
        },
        {
          id: 'bar-charts',
          name: 'Creating and Interpreting Bar Charts',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `bar-${i + 1}`,
            question: i === 0 ? 'Create a bar chart for the following data: Product A sold 45 units, Product B sold 30 units, Product C sold 55 units, Product D sold 25 units. Which product had the highest sales?' :
                     i === 1 ? 'A bar chart shows test scores: A (15 students), B (20 students), C (10 students), D (3 students), F (2 students). What percentage of students earned an A or B?' :
                     i === 2 ? 'Given a bar chart of favorite sports: Soccer (25), Basketball (30), Baseball (15), Football (20), Tennis (10). What is the most popular sport?' :
                     `Interpret a bar chart showing ${['sales', 'attendance', 'preferences'][i % 3]} data with ${Math.floor(Math.random() * 5) + 3} categories.`,
            answer: i === 0 ? 'Product C had the highest sales with 55 units. Bar heights would be: A=45, B=30, C=55, D=25.' :
                   i === 1 ? '(15 + 20) / (15 + 20 + 10 + 3 + 2) = 35/50 = 0.70 or 70% earned an A or B.' :
                   i === 2 ? 'Basketball is the most popular sport with 30 votes, followed by Soccer with 25 votes.' :
                   'Solution with bar chart interpretation and relevant calculations.',
            steps: [
              'Identify all categories and their frequencies',
              'Determine the appropriate scale for the vertical axis',
              'Draw bars with heights proportional to frequencies',
              'Label axes clearly and add a title'
            ]
          }))
        },
        {
          id: 'pie-charts',
          name: 'Creating and Interpreting Pie Charts',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `pie-${i + 1}`,
            question: i === 0 ? 'Create a pie chart for transportation methods: Car 40%, Bus 30%, Walk 20%, Bike 10%. Calculate the angle for each slice.' :
                     i === 1 ? 'A pie chart shows budget allocation: Housing 35%, Food 25%, Transportation 15%, Entertainment 10%, Savings 15%. If total budget is $4000, how much is allocated to food?' :
                     i === 2 ? 'Given a pie chart of favorite colors: Blue 120°, Red 90°, Green 75°, Yellow 45°, Other 30°. What percentage prefers blue?' :
                     `Calculate pie chart angles for ${Math.floor(Math.random() * 4) + 3} categories with given percentages.`,
            answer: i === 0 ? 'Angles: Car = 0.40 × 360° = 144°, Bus = 0.30 × 360° = 108°, Walk = 0.20 × 360° = 72°, Bike = 0.10 × 360° = 36°' :
                   i === 1 ? 'Food allocation = 25% of $4000 = 0.25 × $4000 = $1000' :
                   i === 2 ? 'Blue percentage = 120°/360° = 0.333... ≈ 33.3%' :
                   'Solution with angle calculations and percentages for each slice.',
            steps: [
              'Calculate the percentage for each category',
              'Convert percentages to angles (percentage × 360°)',
              'Draw circle and divide into slices with calculated angles',
              'Label each slice with category name and percentage'
            ]
          }))
        }
      ]
    },
    {
      id: 'quantitative-data',
      title: 'Representing Quantitative Data',
      description: 'Dotplots, stemplots, histograms, and distributions',
      skills: [
        {
          id: 'dotplots',
          name: 'Creating and Interpreting Dotplots',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `dot-${i + 1}`,
            question: i === 0 ? 'Create a dotplot for the following test scores: 85, 90, 85, 92, 88, 85, 90, 95, 88, 90. Describe the distribution.' :
                     i === 1 ? 'A dotplot shows the number of pets: 0 (5 students), 1 (8 students), 2 (6 students), 3 (2 students), 4 (1 student). What is the mode?' :
                     i === 2 ? 'Given a dotplot of daily temperatures: 65°F (2 days), 68°F (3 days), 70°F (5 days), 72°F (4 days), 75°F (1 day). What is the median temperature?' :
                     `Analyze a dotplot showing ${Math.floor(Math.random() * 20) + 10} data points.`,
            answer: i === 0 ? 'Dotplot shows: 85 (3 dots), 88 (2 dots), 90 (3 dots), 92 (1 dot), 95 (1 dot). Distribution is roughly symmetric with a slight right skew, centered around 88-90.' :
                   i === 1 ? 'Mode = 1 pet (appears 8 times, more than any other value)' :
                   i === 2 ? 'Total days = 15. Median is 8th value when ordered: 65,65,68,68,68,70,70,[70],70,70,72,72,72,72,75. Median = 70°F' :
                   'Solution with dotplot description and distribution characteristics.',
            steps: [
              'Order data values from smallest to largest',
              'Draw a number line with appropriate scale',
              'Place a dot above each data value',
              'Describe shape (symmetric, skewed, uniform), center, and spread'
            ]
          }))
        },
        {
          id: 'stemplots',
          name: 'Constructing Stemplots (Stem-and-Leaf Plots)',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `stem-${i + 1}`,
            question: i === 0 ? 'Create a stemplot for: 23, 25, 27, 31, 33, 35, 38, 42, 45, 48, 51, 53. Use stems of 2, 3, 4, 5.' :
                     i === 1 ? 'Given stemplot:\n2 | 3 5 7\n3 | 1 3 5 8\n4 | 2 5 8\n5 | 1 3\nWhat is the range of the data?' :
                     i === 2 ? 'Create a back-to-back stemplot comparing Class A scores (75, 82, 88, 91, 95) to Class B scores (70, 78, 85, 89, 92).' :
                     `Construct a stemplot for ${Math.floor(Math.random() * 10) + 10} two-digit numbers.`,
            answer: i === 0 ? 'Stemplot:\n2 | 3 5 7\n3 | 1 3 5 8\n4 | 2 5 8\n5 | 1 3\nKey: 2|3 means 23' :
                   i === 1 ? 'Smallest value = 23, Largest value = 53, Range = 53 - 23 = 30' :
                   i === 2 ? 'Back-to-back stemplot:\nClass A | Stem | Class B\n        5 |  7  | 0 8\n        2 |  8  | 5 9\n      5 1 |  9  | 2\nKey: 5|7 means 75 for Class A, 7|0 means 70 for Class B' :
                   'Solution with properly formatted stemplot and key.',
            steps: [
              'Determine the stem values (usually tens place)',
              'List stems in a vertical column',
              'Place leaf values (ones place) next to corresponding stems',
              'Order leaves from smallest to largest on each line',
              'Include a key explaining notation'
            ]
          }))
        },
        {
          id: 'histograms',
          name: 'Creating and Interpreting Histograms',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `hist-${i + 1}`,
            question: i === 0 ? 'Create a histogram with class intervals [0-10), [10-20), [20-30), [30-40) for data: 5,8,12,15,18,22,25,28,32,35,38. Use frequency on y-axis.' :
                     i === 1 ? 'A histogram shows: [50-60): 5, [60-70): 12, [70-80): 20, [80-90): 15, [90-100): 8. What percentage of data falls in [70-90)?' :
                     i === 2 ? 'Given a right-skewed histogram of incomes. Explain why median would be less than mean.' :
                     `Interpret a histogram showing ${['test scores', 'ages', 'heights'][i % 3]} with ${Math.floor(Math.random() * 3) + 4} bins.`,
            answer: i === 0 ? 'Histogram frequencies:\n[0-10): 2 (values 5, 8)\n[10-20): 3 (values 12, 15, 18)\n[20-30): 3 (values 22, 25, 28)\n[30-40): 3 (values 32, 35, 38)' :
                   i === 1 ? 'Data in [70-90) = 20 + 15 = 35. Total = 5+12+20+15+8 = 60. Percentage = 35/60 ≈ 58.3%' :
                   i === 2 ? 'In a right-skewed distribution, the tail pulls the mean to the right, making it larger than the median. The median is resistant to outliers, while the mean is affected by extreme high values.' :
                   'Solution with histogram construction and interpretation.',
            steps: [
              'Determine appropriate class intervals (bins)',
              'Count frequency for each interval',
              'Draw bars with heights equal to frequencies (no gaps between bars)',
              'Label axes and describe shape (symmetric, skewed left/right, uniform)'
            ]
          }))
        },
        {
          id: 'distribution-shape',
          name: 'Describing Distribution Shape',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `shape-${i + 1}`,
            question: i === 0 ? 'Describe the shape of a distribution where most values are in the middle with few extreme values on both sides.' :
                     i === 1 ? 'A histogram shows more data on the right with a tail extending to the left. Identify the skewness and predict the relationship between mean and median.' :
                     i === 2 ? 'Test scores: 65, 70, 72, 75, 75, 78, 80, 80, 82, 85, 90, 95, 98. Is this distribution symmetric, left-skewed, or right-skewed?' :
                     `Identify the shape of a distribution based on ${['histogram', 'dotplot', 'stemplot'][i % 3]} characteristics.`,
            answer: i === 0 ? 'This is a symmetric (or approximately normal/bell-shaped) distribution. Data is evenly distributed around the center.' :
                   i === 1 ? 'This is a left-skewed (negatively skewed) distribution. The tail extends to the left. Mean < Median because the tail pulls the mean toward lower values.' :
                   i === 2 ? 'This distribution is roughly symmetric. Most scores cluster around the middle (75-80), with similar spread on both sides. Mean ≈ Median ≈ 79.' :
                   'Solution identifying distribution shape and its characteristics.',
            steps: [
              'Examine where data is concentrated',
              'Look for tails extending left or right',
              'Symmetric: mirror image on both sides of center',
              'Right-skewed: tail extends right (Mean > Median)',
              'Left-skewed: tail extends left (Mean < Median)'
            ]
          }))
        }
      ]
    },
    {
      id: 'center-spread',
      title: 'Measuring Center and Spread',
      description: 'Mean, median, mode, range, IQR, standard deviation, variance',
      skills: [
        {
          id: 'measures-center',
          name: 'Calculating Mean, Median, and Mode',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `center-${i + 1}`,
            question: i === 0 ? 'Calculate the mean, median, and mode for: 12, 15, 15, 18, 20, 22, 25, 15, 30.' :
                     i === 1 ? 'A student scored 85, 90, 78, 92, 88 on five tests. What is the mean score?' :
                     i === 2 ? 'Data set: 5, 10, 15, 20, 25, 30. Calculate the median. Then add a value of 100. How does the median change?' :
                     `Calculate measures of center for a dataset with ${Math.floor(Math.random() * 8) + 5} values.`,
            answer: i === 0 ? 'Mean = (12+15+15+18+20+22+25+15+30)/9 = 172/9 ≈ 19.11\nMedian: Ordered: 12,15,15,15,18,20,22,25,30. Middle value = 18\nMode = 15 (appears 3 times)' :
                   i === 1 ? 'Mean = (85+90+78+92+88)/5 = 433/5 = 86.6' :
                   i === 2 ? 'Original median = (15+20)/2 = 17.5. With 100 added: 5,10,15,20,25,30,100. New median = 20. The median changed from 17.5 to 20.' :
                   'Solution with calculations for mean, median, and mode.',
            steps: [
              'Mean: Sum all values and divide by count',
              'Median: Order data, find middle value (or average of two middle values)',
              'Mode: Identify most frequently occurring value(s)',
              'Note: Mean affected by outliers, median resistant to outliers'
            ]
          }))
        },
        {
          id: 'range-iqr',
          name: 'Calculating Range and Interquartile Range (IQR)',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `iqr-${i + 1}`,
            question: i === 0 ? 'Find Q1, Q3, IQR, and range for: 10, 15, 20, 25, 30, 35, 40, 45, 50.' :
                     i === 1 ? 'Given: 8, 12, 15, 18, 20, 22, 25, 28, 30, 35, 40. Calculate the IQR.' :
                     i === 2 ? 'A dataset has Q1 = 45, Q3 = 75. Calculate IQR and identify potential outliers using 1.5×IQR rule.' :
                     `Calculate IQR for a dataset with ${Math.floor(Math.random() * 10) + 9} values.`,
            answer: i === 0 ? 'Ordered: 10,15,20,25,30,35,40,45,50\nMedian = 30\nQ1 = median of lower half (10,15,20,25) = (15+20)/2 = 17.5\nQ3 = median of upper half (35,40,45,50) = (40+45)/2 = 42.5\nIQR = Q3 - Q1 = 42.5 - 17.5 = 25\nRange = 50 - 10 = 40' :
                   i === 1 ? 'n=11, Median=22\nLower half: 8,12,15,18,20 → Q1=15\nUpper half: 25,28,30,35,40 → Q3=30\nIQR = 30-15 = 15' :
                   i === 2 ? 'IQR = 75 - 45 = 30\nLower fence = Q1 - 1.5(IQR) = 45 - 1.5(30) = 45 - 45 = 0\nUpper fence = Q3 + 1.5(IQR) = 75 + 1.5(30) = 75 + 45 = 120\nAny value < 0 or > 120 is a potential outlier.' :
                   'Solution with Q1, Q3, IQR calculations and outlier identification.',
            steps: [
              'Order the data from smallest to largest',
              'Find median (Q2)',
              'Q1 = median of lower half, Q3 = median of upper half',
              'IQR = Q3 - Q1',
              'Outliers: values < Q1-1.5(IQR) or > Q3+1.5(IQR)'
            ]
          }))
        },
        {
          id: 'standard-deviation',
          name: 'Calculating Standard Deviation and Variance',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `sd-${i + 1}`,
            question: i === 0 ? 'Calculate the sample standard deviation for: 10, 12, 15, 18, 20. Show all steps.' :
                     i === 1 ? 'Two datasets: A = {5, 10, 15, 20, 25} and B = {10, 12, 13, 14, 16}. Which has larger standard deviation and why?' :
                     i === 2 ? 'Given: mean = 50, variance = 100. What is the standard deviation? If each value increases by 10, what happens to mean and standard deviation?' :
                     `Calculate variance and standard deviation for ${Math.floor(Math.random() * 5) + 4} data values.`,
            answer: i === 0 ? 'Step 1: Mean = (10+12+15+18+20)/5 = 15\nStep 2: Deviations: (10-15)²=25, (12-15)²=9, (15-15)²=0, (18-15)²=9, (20-15)²=25\nStep 3: Sum of squared deviations = 25+9+0+9+25 = 68\nStep 4: Sample variance s² = 68/(5-1) = 68/4 = 17\nStep 5: Sample standard deviation s = √17 ≈ 4.12' :
                   i === 1 ? 'Dataset A has larger standard deviation. A spans 5 to 25 (range 20), while B spans 10 to 16 (range 6). A is more spread out, so larger SD.' :
                   i === 2 ? 'Standard deviation = √100 = 10. If each value increases by 10: New mean = 50+10 = 60, but standard deviation remains 10 (shifting all values doesn\'t change spread).' :
                   'Solution with detailed variance and standard deviation calculations.',
            steps: [
              'Calculate the mean',
              'Find each deviation from mean (x - x̄)',
              'Square each deviation',
              'Sum all squared deviations',
              'Divide by (n-1) for sample variance',
              'Take square root to get standard deviation'
            ]
          }))
        },
        {
          id: 'effects-transformations',
          name: 'Effects of Adding/Multiplying Constants',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `trans-${i + 1}`,
            question: i === 0 ? 'Original data: mean=50, SD=10. If 5 is added to each value, find new mean and SD.' :
                     i === 1 ? 'Data has mean=20, SD=4. Each value is multiplied by 3. Find new mean and SD.' :
                     i === 2 ? 'Temperature in °C: mean=25, SD=5. Convert to °F using F = (9/5)C + 32. Find mean and SD in °F.' :
                     `Analyze the effect of ${['adding', 'multiplying', 'transforming'][i % 3]} data values on center and spread.`,
            answer: i === 0 ? 'Adding 5 to each value: New mean = 50+5 = 55, New SD = 10 (unchanged, because adding shifts center but not spread).' :
                   i === 1 ? 'Multiplying by 3: New mean = 20×3 = 60, New SD = 4×3 = 12 (both center and spread scale by the same factor).' :
                   i === 2 ? 'F = (9/5)C + 32\nNew mean = (9/5)(25) + 32 = 45 + 32 = 77°F\nNew SD = (9/5)(5) = 9°F (adding 32 doesn\'t affect SD, but multiplying by 9/5 does).' :
                   'Solution explaining effects on measures of center and spread.',
            steps: [
              'Adding a constant: Mean shifts by that constant, SD unchanged',
              'Multiplying by constant: Mean and SD both multiply by |constant|',
              'General transformation y = a + bx: mean_y = a + b(mean_x), SD_y = |b|(SD_x)'
            ]
          }))
        }
      ]
    },
    {
      id: 'comparing-distributions',
      title: 'Comparing Distributions',
      description: 'Boxplots, comparing shape/center/spread, outliers',
      skills: [
        {
          id: 'boxplots',
          name: 'Creating and Interpreting Boxplots',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `box-${i + 1}`,
            question: i === 0 ? 'Create a boxplot for: 15, 18, 20, 22, 25, 28, 30, 35, 40, 45, 60. Identify any outliers.' :
                     i === 1 ? 'A boxplot shows: Min=10, Q1=20, Median=30, Q3=40, Max=50. Interpret the symmetry of the distribution.' :
                     i === 2 ? 'Given two boxplots comparing Class A and Class B test scores. Class A has higher median but wider IQR. What does this tell you?' :
                     `Interpret a boxplot showing ${['salary', 'age', 'height'][i % 3]} data with ${Math.floor(Math.random() * 3) + 5} summary statistics.`,
            answer: i === 0 ? 'Five-number summary:\nMin=15, Q1=21, Median=28, Q3=37.5, Max=60\nIQR=37.5-21=16.5\nUpper fence=37.5+1.5(16.5)=62.25\n60 < 62.25, so no outliers\nBoxplot would show box from 21 to 37.5 with median line at 28.' :
                   i === 1 ? 'The distribution appears symmetric. Distance from Q1 to Median (30-20=10) equals distance from Median to Q3 (40-30=10). Also, Min to Q1 (20-10=10) equals Q3 to Max (50-40=10).' :
                   i === 2 ? 'Class A has higher typical performance (higher median) but more variability in scores (wider IQR). Class B is more consistent but with lower overall performance.' :
                   'Solution with boxplot construction and interpretation.',
            steps: [
              'Calculate five-number summary: Min, Q1, Median, Q3, Max',
              'Draw box from Q1 to Q3 with line at median',
              'Draw whiskers to Min and Max (excluding outliers)',
              'Mark outliers as individual points beyond fences',
              'Compare distributions using center, spread, and shape'
            ]
          }))
        },
        {
          id: 'comparing-groups',
          name: 'Comparing Center and Spread',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `compare-${i + 1}`,
            question: i === 0 ? 'Group A: mean=75, SD=8. Group B: mean=82, SD=12. Which group performed better? Which is more consistent?' :
                     i === 1 ? 'Two distributions have same mean (50) but different SDs (5 vs 15). Explain what this means practically.' :
                     i === 2 ? 'Compare test scores: Class X has median=85, IQR=10. Class Y has median=80, IQR=5. Interpret.' :
                     `Compare two groups based on measures of center (${['mean', 'median'][i % 2]}) and spread (${['SD', 'IQR'][i % 2]}).`,
            answer: i === 0 ? 'Group B performed better on average (mean 82 > 75). Group A is more consistent with smaller variability (SD 8 < 12). Group B has higher scores but more spread out.' :
                   i === 1 ? 'Both groups have the same typical value (mean=50), but one is much more variable. SD=5 means values tightly clustered around 50, while SD=15 means values are much more spread out.' :
                   i === 2 ? 'Class X has higher typical scores (median 85 vs 80) but more variability (IQR 10 vs 5). Class Y is more consistent with scores closer together, but overall lower performance.' :
                   'Solution comparing center, spread, and consistency between groups.',
            steps: [
              'Compare centers: higher mean/median indicates higher typical value',
              'Compare spreads: smaller SD/IQR indicates more consistency',
              'Consider both together: trade-off between performance and consistency',
              'Use context to interpret practical significance'
            ]
          }))
        }
      ]
    }
  ]
};

export const unit2: Unit = {
  id: 'unit2',
  title: 'Unit 2: Exploring Two-Variable Data',
  description: 'Scatterplots, correlation, least-squares regression lines, and residuals',
  weight: '5%–7%',
  topics: [
    {
      id: 'scatterplots',
      title: 'Scatterplots and Association',
      description: 'Creating scatterplots, identifying direction, form, and strength',
      skills: [
        {
          id: 'creating-scatterplots',
          name: 'Creating and Interpreting Scatterplots',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `scatter-${i + 1}`,
            question: i === 0 ? 'Create a scatterplot for: (1,3), (2,5), (3,7), (4,9), (5,11). Describe the direction and form.' :
                     i === 1 ? 'A scatterplot shows hours studied (x) vs test score (y). Points trend upward from left to right. What is the direction of association?' :
                     i === 2 ? 'Given scatterplot of temperature vs ice cream sales. Describe the relationship you would expect.' :
                     `Interpret a scatterplot showing relationship between ${['age and income', 'study time and grades', 'price and demand'][i % 3]}.`,
            answer: i === 0 ? 'Scatterplot shows perfect positive linear relationship. All points lie on a straight line with positive slope. Direction: positive, Form: linear, Strength: strong (perfect).' :
                   i === 1 ? 'The direction is positive. As hours studied increases, test scores tend to increase.' :
                   i === 2 ? 'Expected positive association: As temperature increases, ice cream sales tend to increase. Form would likely be linear with moderate strength.' :
                   'Solution describing direction, form, and strength of association.',
            steps: [
              'Plot points with explanatory variable on x-axis, response on y-axis',
              'Direction: positive (upward trend) or negative (downward trend)',
              'Form: linear, curved, or no pattern',
              'Strength: strong (tight pattern), moderate, or weak (scattered)',
              'Note any outliers or unusual observations'
            ]
          }))
        },
        {
          id: 'outliers-influential',
          name: 'Identifying Outliers and Influential Points',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `outlier-${i + 1}`,
            question: i === 0 ? 'Data: (1,2), (2,4), (3,6), (4,8), (5,20). Identify the outlier. How does it affect the regression line?' :
                     i === 1 ? 'What is the difference between an outlier and an influential point in regression?' :
                     i === 2 ? 'A scatterplot shows strong positive trend with one point far from the pattern. If removed, how would correlation likely change?' :
                     `Identify ${['outliers', 'influential points'][i % 2]} in a scatterplot and assess their impact.`,
            answer: i === 0 ? 'Point (5,20) is an outlier - it doesn\'t follow the linear pattern of other points. It would pull the regression line upward, increasing slope and decreasing fit quality.' :
                   i === 1 ? 'An outlier is a point far from the overall pattern (unusual y-value for its x-value). An influential point is one whose removal would substantially change the regression line (often has unusual x-value).' :
                   i === 2 ? 'If the outlier is removed, the correlation would likely become stronger (closer to ±1) because the point disrupts the otherwise tight linear pattern.' :
                   'Solution identifying outliers/influential points and explaining their effects.',
            steps: [
              'Identify points that don\'t follow the general pattern',
              'Outliers: far from regression line vertically',
              'Influential points: have unusual x-values (far from x̄)',
              'Assess impact by comparing regression with and without the point',
              'Use residual plots to help identify outliers'
            ]
          }))
        }
      ]
    },
    {
      id: 'correlation',
      title: 'Correlation',
      description: 'Calculating and interpreting correlation coefficient',
      skills: [
        {
          id: 'correlation-coefficient',
          name: 'Calculating Correlation Coefficient (r)',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `corr-${i + 1}`,
            question: i === 0 ? 'Calculate correlation coefficient for: x = [1,2,3,4,5], y = [2,4,6,8,10].' :
                     i === 1 ? 'Given: r = 0.85. Interpret the strength and direction of the linear relationship.' :
                     i === 2 ? 'Two variables have r = -0.92. What percentage of variation in y is explained by x?' :
                     `Interpret correlation coefficient r = ${(Math.random() * 2 - 1).toFixed(2)} for a given relationship.`,
            answer: i === 0 ? 'Perfect positive linear relationship, so r = 1.00. Formula: r = Σ[(x-x̄)(y-ȳ)] / √[Σ(x-x̄)²Σ(y-ȳ)²]. With perfect linear fit, numerator equals denominator.' :
                   i === 1 ? 'r = 0.85 indicates a strong positive linear relationship. Variables move together in the same direction, and points are tightly clustered around the regression line.' :
                   i === 2 ? 'r² = (-0.92)² = 0.8464 or 84.64% of the variation in y is explained by the linear relationship with x. This is a very strong negative association.' :
                   'Solution with correlation calculation and interpretation.',
            steps: [
              'Standardize both variables (convert to z-scores)',
              'Multiply paired z-scores',
              'Average the products: r = Σ(z_x × z_y) / (n-1)',
              'r ranges from -1 to +1',
              'r = 0: no linear relationship',
              '|r| close to 1: strong linear relationship'
            ]
          }))
        },
        {
          id: 'properties-correlation',
          name: 'Properties of Correlation',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `prop-${i + 1}`,
            question: i === 0 ? 'If x is measured in inches and y in pounds, then x is converted to feet, how does r change?' :
                     i === 1 ? 'Can correlation coefficient be used to describe a curved relationship? Explain.' :
                     i === 2 ? 'Given strong positive association with r = 0.90. Can we conclude x causes y? Why or why not?' :
                     `Explain properties of correlation in ${['unit changes', 'nonlinear relationships', 'causation'][i % 3]} context.`,
            answer: i === 0 ? 'r does not change. Correlation is unitless and unaffected by linear transformations (changing units). Converting inches to feet multiplies by 1/12, but r remains the same.' :
                   i === 1 ? 'No, correlation measures only linear relationships. A curved relationship might have r ≈ 0 even if variables are strongly related. Always examine scatterplot to check linearity.' :
                   i === 2 ? 'No, correlation does not imply causation. r = 0.90 shows strong association, but could be due to: lurking variable, reverse causation, or coincidence. Need controlled experiment to establish causation.' :
                   'Solution explaining key properties and limitations of correlation.',
            steps: [
              'r is unitless (not affected by changing units)',
              'r measures only linear relationships',
              'r is sensitive to outliers',
              'Correlation ≠ Causation',
              'Always plot data before computing r'
            ]
          }))
        }
      ]
    },
    {
      id: 'regression',
      title: 'Least-Squares Regression',
      description: 'Finding regression line, predictions, residuals',
      skills: [
        {
          id: 'lsrl',
          name: 'Finding Least-Squares Regression Line (LSRL)',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `lsrl-${i + 1}`,
            question: i === 0 ? 'Given: x̄=5, ȳ=20, s_x=2, s_y=8, r=0.75. Find the LSRL equation ŷ = a + bx.' :
                     i === 1 ? 'Data: (1,3), (2,5), (3,7), (4,9). Calculate slope and y-intercept of LSRL.' :
                     i === 2 ? 'Regression line: ŷ = 15 + 2.5x. Interpret the slope in context of "x=hours studied, y=test score".' :
                     `Find LSRL for dataset with ${Math.floor(Math.random() * 5) + 4} points.`,
            answer: i === 0 ? 'Slope b = r(s_y/s_x) = 0.75(8/2) = 0.75(4) = 3\nIntercept a = ȳ - b(x̄) = 20 - 3(5) = 20 - 15 = 5\nLSRL: ŷ = 5 + 3x' :
                   i === 1 ? 'x̄=2.5, ȳ=6, Σ(x-x̄)(y-ȳ)=5, Σ(x-x̄)²=5\nb=Σ(x-x̄)(y-ȳ)/Σ(x-x̄)²=5/5=2\na=ȳ-b(x̄)=6-2(2.5)=1\nLSRL: ŷ=1+2x (or y=2x+1)' :
                   i === 2 ? 'Slope = 2.5 means: For each additional hour studied, the predicted test score increases by 2.5 points on average.' :
                   'Solution with LSRL equation and interpretation.',
            steps: [
              'Calculate slope: b = r(s_y/s_x)',
              'Calculate y-intercept: a = ȳ - b(x̄)',
              'Write equation: ŷ = a + bx',
              'Interpret slope: change in y per unit change in x',
              'Interpret y-intercept: predicted y when x = 0 (if meaningful)',
              'Use equation to make predictions'
            ]
          }))
        },
        {
          id: 'residuals',
          name: 'Calculating and Interpreting Residuals',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `resid-${i + 1}`,
            question: i === 0 ? 'LSRL: ŷ = 10 + 2x. For point (5, 25), calculate the residual.' :
                     i === 1 ? 'A residual plot shows random scatter around zero. What does this indicate about the regression model?' :
                     i === 2 ? 'Given residual = -3 for a data point. Did the model overpredict or underpredict? By how much?' :
                     `Calculate residuals and interpret ${['residual plots', 'prediction errors', 'model fit'][i % 3]}.`,
            answer: i === 0 ? 'Predicted: ŷ = 10 + 2(5) = 10 + 10 = 20\nActual: y = 25\nResidual = y - ŷ = 25 - 20 = 5\nThe model underpredicted by 5 units.' :
                   i === 1 ? 'Random scatter indicates the linear model is appropriate. No systematic pattern means residuals are independent and linear assumption is satisfied.' :
                   i === 2 ? 'Residual = -3 means y - ŷ = -3, so y < ŷ. The model overpredicted by 3 units (predicted value was 3 units too high).' :
                   'Solution with residual calculations and model assessment.',
            steps: [
              'Calculate predicted value: ŷ = a + bx',
              'Calculate residual: residual = y - ŷ (observed - predicted)',
              'Positive residual: model underpredicted',
              'Negative residual: model overpredicted',
              'Plot residuals vs x to check for patterns',
              'Random scatter = good fit; pattern = poor fit'
            ]
          }))
        },
        {
          id: 'r-squared',
          name: 'Coefficient of Determination (r²)',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `rsq-${i + 1}`,
            question: i === 0 ? 'Given r = 0.80, calculate and interpret r².' :
                     i === 1 ? 'A regression has r² = 0.64. What does this tell us about the model?' :
                     i === 2 ? 'Can r² be negative? Why or why not?' :
                     `Interpret r² = ${(Math.random() * 0.8 + 0.2).toFixed(2)} in context.`,
            answer: i === 0 ? 'r² = (0.80)² = 0.64 or 64%. This means 64% of the variation in the response variable is explained by the linear relationship with the explanatory variable.' :
                   i === 1 ? 'r² = 0.64 means 64% of variation is explained by the model, leaving 36% unexplained. Moderate fit - model explains more than half the variation but substantial variation remains.' :
                   i === 2 ? 'No, r² cannot be negative. It equals the square of r, so minimum value is 0 (no linear relationship). r² = 0 means the line explains none of the variation in y.' :
                   'Solution explaining coefficient of determination.',
            steps: [
              'Calculate: r² = (correlation)²',
              'Interpret: percentage of variation in y explained by x',
              'r² = 1: perfect fit, all points on line',
              'r² = 0: no linear relationship',
              'Higher r² indicates better fit'
            ]
          }))
        }
      ]
    }
  ]
};

export const unit3: Unit = {
  id: 'unit3',
  title: 'Unit 3: Collecting Data',
  description: 'Designing studies, sampling methods, and experiments',
  weight: '12%–15%',
  topics: [
    {
      id: 'sampling',
      title: 'Sampling Methods',
      description: 'Census, random sampling, stratified, cluster, systematic',
      skills: [
        {
          id: 'simple-random-sample',
          name: 'Simple Random Sample (SRS)',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `srs-${i + 1}`,
            question: i === 0 ? 'Explain how to select a simple random sample of 20 students from a school of 500 students.' :
                     i === 1 ? 'What is the key characteristic that distinguishes an SRS from other sampling methods?' :
                     i === 2 ? 'A researcher assigns each of 100 patients a number 1-100, then uses random number generator to select 25. Is this an SRS?' :
                     `Design an SRS procedure for selecting ${Math.floor(Math.random() * 30) + 10} individuals from a population.`,
            answer: i === 0 ? 'Procedure: 1) Create sampling frame listing all 500 students. 2) Assign each student a number 001-500. 3) Use random number generator to select 20 numbers. 4) Sample consists of students with those numbers. Every possible group of 20 has equal probability of selection.' :
                   i === 1 ? 'In an SRS, every individual and every possible combination of n individuals has an equal probability of being selected. Selection is purely random with no systematic pattern.' :
                   i === 2 ? 'Yes, this is an SRS. Each patient had equal probability of selection (25/100 = 0.25), and selection was random. This satisfies the definition of simple random sampling.' :
                   'Solution describing SRS procedure and characteristics.',
            steps: [
              'Create complete list (sampling frame) of population',
              'Assign unique number to each individual',
              'Use random mechanism to select sample',
              'Every individual has equal selection probability',
              'Advantage: unbiased, representative'
            ]
          }))
        },
        {
          id: 'stratified-cluster',
          name: 'Stratified and Cluster Sampling',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `strat-${i + 1}`,
            question: i === 0 ? 'School has 60% freshmen, 40% sophomores. Design stratified random sample of 100 students.' :
                     i === 1 ? 'Explain the difference between stratified and cluster sampling.' :
                     i === 2 ? 'A state has 50 counties. Randomly select 5 counties, then survey all residents in those 5. What sampling method is this?' :
                     `Design a ${['stratified', 'cluster'][i % 2]} sampling plan for a given population.`,
            answer: i === 0 ? 'Stratified sample: Select 60 freshmen (60% of 100) and 40 sophomores (40% of 100) using SRS within each grade level. This ensures representation proportional to population.' :
                   i === 1 ? 'Stratified: Divide population into homogeneous groups (strata), then SRS from each stratum. Sample from all strata.\nCluster: Divide into clusters, randomly select some clusters, sample all/some from selected clusters. More convenient but less precise.' :
                   i === 2 ? 'This is cluster sampling. Counties are clusters, and we randomly selected entire clusters (counties) to survey. We didn\'t sample from all counties, just the selected ones.' :
                   'Solution explaining sampling method and implementation.',
            steps: [
              'Stratified: Group by characteristic → SRS from each group',
              'Cluster: Divide into clusters → randomly select clusters → sample within clusters',
              'Stratified reduces variability (strata are homogeneous)',
              'Cluster is more practical/economical',
              'Both start with random selection'
            ]
          }))
        },
        {
          id: 'sampling-bias',
          name: 'Sources of Bias in Sampling',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `bias-${i + 1}`,
            question: i === 0 ? 'A phone survey only calls landlines between 9am-5pm. Identify potential sources of bias.' :
                     i === 1 ? 'Explain the difference between undercoverage and nonresponse bias.' :
                     i === 2 ? 'A survey asks: "Do you support the wasteful spending on Project X?" What type of bias is present?' :
                     `Identify and explain bias in ${['online surveys', 'volunteer samples', 'question wording'][i % 3]}.`,
            answer: i === 0 ? 'Sources of bias:\n1) Undercoverage: Excludes cell-phone-only households (typically younger people)\n2) Nonresponse: People at work during 9-5 can\'t answer\n3) Results will overrepresent retirees/stay-at-home individuals.' :
                   i === 1 ? 'Undercoverage: Some groups in population have no chance of selection (not in sampling frame). Example: phone book excludes unlisted numbers.\nNonresponse: Selected individuals don\'t respond. Example: people refuse to answer survey or can\'t be reached.' :
                   i === 2 ? 'Response bias - specifically, question wording bias. Using "wasteful spending" influences respondents toward a negative answer. Question should be neutral: "Do you support Project X?"' :
                   'Solution identifying specific bias types and their effects.',
            steps: [
              'Undercoverage: systematic exclusion of groups',
              'Nonresponse: selected individuals don\'t participate',
              'Response bias: question wording, interviewer influence',
              'Voluntary response: self-selected sample (usually biased)',
              'All bias types lead to unrepresentative samples'
            ]
          }))
        }
      ]
    },
    {
      id: 'experiments',
      title: 'Designing Experiments',
      description: 'Randomization, control, replication, blocking',
      skills: [
        {
          id: 'experimental-design',
          name: 'Basic Experimental Design Principles',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `exp-${i + 1}`,
            question: i === 0 ? 'Explain the three principles of experimental design: randomization, control, and replication.' :
                     i === 1 ? 'What is the difference between an observational study and an experiment?' :
                     i === 2 ? 'Design an experiment to test whether a new fertilizer increases plant growth. Include control group and randomization.' :
                     `Design an experiment to test ${['new drug', 'teaching method', 'diet plan'][i % 3]} effectiveness.`,
            answer: i === 0 ? '1) Randomization: Randomly assign subjects to treatment groups to balance lurking variables.\n2) Control: Use control group or comparison treatment to isolate treatment effect.\n3) Replication: Use enough subjects to distinguish treatment effects from chance variation. Apply treatments to multiple units.' :
                   i === 1 ? 'Observational study: Researcher observes without intervention. Can show association but not causation (confounding possible).\nExperiment: Researcher actively imposes treatments. Random assignment allows causal conclusions because confounding is controlled.' :
                   i === 2 ? 'Design:\n1) Obtain 40 similar plants\n2) Randomly assign 20 to new fertilizer (treatment) and 20 to current fertilizer (control)\n3) Keep all other conditions identical (water, sunlight, soil)\n4) Measure growth after 4 weeks\n5) Compare treatment vs control using statistical test' :
                   'Solution with complete experimental design including all principles.',
            steps: [
              'Identify treatments and response variable',
              'Randomly assign subjects to treatments',
              'Include control/comparison group',
              'Use sufficient sample size (replication)',
              'Keep all other variables constant',
              'Measure response and compare groups'
            ]
          }))
        },
        {
          id: 'blocking-matching',
          name: 'Blocking and Matched Pairs',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `block-${i + 1}`,
            question: i === 0 ? 'Researchers test two reading programs with 60 students: 30 strong readers, 30 weak readers. Should they use blocking? Explain.' :
                     i === 1 ? 'Describe a matched pairs experimental design. What is its main advantage?' :
                     i === 2 ? 'Design a blocked experiment to test three fertilizers on 60 plants, with blocks based on sunlight exposure (full sun, partial shade, shade).' :
                     `Design a ${['blocked', 'matched pairs'][i % 2]} experiment for testing ${Math.floor(Math.random() * 3) + 2} treatments.`,
            answer: i === 0 ? 'Yes, use blocking. Reading ability affects response to programs (lurking variable). Design:\nBlock 1 (strong readers): Randomly assign 15 to Program A, 15 to Program B\nBlock 2 (weak readers): Randomly assign 15 to Program A, 15 to Program B\nBlocking reduces variability and increases power to detect differences.' :
                   i === 1 ? 'Matched pairs: Each subject receives both treatments (in random order) OR subjects are matched in pairs and one from each pair gets each treatment. Advantage: Controls for individual differences, increasing precision. Each subject serves as own control.' :
                   i === 2 ? 'Blocked Design:\nBlock 1 (Full sun): 20 plants randomly assigned to 3 fertilizers\nBlock 2 (Partial shade): 20 plants randomly assigned to 3 fertilizers\nBlock 3 (Shade): 20 plants randomly assigned to 3 fertilizers\nCompare fertilizers within each block, then combine results. Controls for sunlight effect.' :
                   'Solution with detailed blocked/matched pairs design.',
            steps: [
              'Blocking: Group subjects by characteristic expected to affect response',
              'Randomly assign treatments within each block',
              'Matched pairs: special case with block size = 2',
              'Blocking reduces variability, increases precision',
              'Still need randomization within blocks'
            ]
          }))
        },
        {
          id: 'confounding-lurking',
          name: 'Confounding and Lurking Variables',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `confound-${i + 1}`,
            question: i === 0 ? 'Study finds people who drink coffee have higher heart disease rates. Explain why we can\'t conclude coffee causes heart disease.' :
                     i === 1 ? 'In an observational study, what is a lurking variable? Give an example.' :
                     i === 2 ? 'How does random assignment in experiments help control for confounding variables?' :
                     `Identify potential confounding variables in ${['observational study', 'experiment', 'survey'][i % 3]}.`,
            answer: i === 0 ? 'This is an observational study, so causation can\'t be established. Possible lurking variables: smoking (coffee drinkers may smoke more), stress (high stress → more coffee and higher disease risk), age, diet. Random assignment is needed to conclude causation.' :
                   i === 1 ? 'Lurking variable: Variable not measured that influences both explanatory and response variables, creating spurious association. Example: Ice cream sales and drowning deaths both increase in summer (temperature is lurking variable).' :
                   i === 2 ? 'Random assignment tends to balance lurking variables across treatment groups. Both known and unknown confounding factors are distributed equally among groups on average, so any observed difference can be attributed to treatment.' :
                   'Solution identifying and explaining confounding variables.',
            steps: [
              'Lurking variable affects both x and y',
              'Creates false appearance of relationship',
              'Observational studies can\'t eliminate confounding',
              'Experiments use randomization to control confounding',
              'Blocking can control known lurking variables'
            ]
          }))
        }
      ]
    }
  ]
};